import React, { useState } from 'react'
import clsx from 'clsx'
import {
  Dialog,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  CircularProgress,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import Button from '../Button/Button'
import TextField from '../TextField/TextField'
import { prismicText } from '../Global/CMSText'
import {
  createContactRequest,
  ContactRequestType,
} from '../../redux/services/contactRequests'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modal: {
      '& .MuiDialog-paper': {
        width: '100%',
        maxWidth: 520,
      },
    },
    head: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      padding: theme.spacing(3, 3, 1, 3),
    },
    title: {
      fontWeight: 500,
      color: theme.palette.primary.main,
    },
    close: {
      cursor: 'pointer',
      marginLeft: theme.spacing(2),
      flexShrink: 0,
    },
    content: {
      padding: theme.spacing(1, 3, 3, 3),
    },
    intro: {
      marginBottom: theme.spacing(2),
    },
    typeGroup: {
      marginBottom: theme.spacing(2),
    },
    typeLabel: {
      marginBottom: theme.spacing(0.5),
    },
    messageField: {
      '& .input': {
        width: '100%',
      },
      '& .MuiInputBase-root': {
        width: '100%',
      },
      '& .MuiInputBase-input, & textarea': {
        width: '100%',
        boxSizing: 'border-box',
      },
    },
    error: {
      color: theme.palette.error.main,
      marginTop: theme.spacing(-1),
      marginBottom: theme.spacing(1.5),
      fontSize: '0.8rem',
    },
    globalError: {
      color: theme.palette.error.main,
      marginTop: theme.spacing(1),
    },
    required: {
      display: 'block',
      marginTop: theme.spacing(1),
      color: theme.palette.text.secondary,
      fontSize: '0.8rem',
    },
    submit: {
      marginTop: theme.spacing(3),
    },
    success: {
      textAlign: 'center',
      padding: theme.spacing(4, 3),
    },
    successIcon: {
      fontSize: 64,
      color: theme.palette.success?.main || theme.palette.primary.main,
      marginBottom: theme.spacing(2),
    },
  })
)

type Tprops = {
  open: boolean
  onClose: () => void
  doc?: any
  t: (key: string) => string
}

const CancelContractModal = ({ open, onClose, doc, t }: Tprops) => {
  const classes = useStyles()
  const data = doc?.data

  const [type, setType] = useState<ContactRequestType>('CANCELLATION')
  const [orderReference, setOrderReference] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [errors, setErrors] = useState<{
    orderReference?: string
    email?: string
    global?: string
  }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const resetAndClose = () => {
    onClose()
    // Reset once the modal is closed so the user does not see it flicker.
    setTimeout(() => {
      setType('CANCELLATION')
      setOrderReference('')
      setEmail('')
      setMessage('')
      setErrors({})
      setIsSuccess(false)
    }, 300)
  }

  const validate = () => {
    const nextErrors: typeof errors = {}
    if (!orderReference.trim()) {
      nextErrors.orderReference = t(
        'texts:cancel_contract:error_order_reference'
      )
    }
    if (!email.trim() || !EMAIL_REGEX.test(email.trim())) {
      nextErrors.email = t('texts:cancel_contract:error_email')
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSubmitting) return
    if (!validate()) return

    setIsSubmitting(true)
    setErrors({})
    try {
      await createContactRequest({
        orderReference: orderReference.trim(),
        email: email.trim(),
        type,
        message: message.trim() || undefined,
      })
      setIsSuccess(true)
    } catch (err) {
      setErrors({ global: t('texts:cancel_contract:error_generic') })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog
      className={clsx('cancel-contract-modal', classes.modal)}
      open={open}
      onClose={resetAndClose}
      maxWidth="sm"
    >
      <div className={clsx('cancel-contract-modal__head', classes.head)}>
        <Typography variant="h5" component="h2" className={classes.title}>
          {prismicText(
            data?.cancel_contract_title,
            t('texts:cancel_contract:title')
          )}
        </Typography>
        <CloseIcon
          className={clsx('cancel-contract-modal__close', classes.close)}
          onClick={resetAndClose}
          role="button"
          aria-label={t('texts:cancel_contract:close_button')}
        />
      </div>

      {isSuccess ? (
        <div
          className={clsx('cancel-contract-modal__success', classes.success)}
        >
          <CheckCircleOutlineIcon className={classes.successIcon} />
          <Typography variant="h6" component="p" gutterBottom>
            {prismicText(
              data?.cancel_contract_success_title,
              t('texts:cancel_contract:success_title')
            )}
          </Typography>
          <Typography component="p">
            {prismicText(
              data?.cancel_contract_success_message,
              t('texts:cancel_contract:success_message')
            )}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            className={classes.submit}
            onClick={resetAndClose}
          >
            {prismicText(
              data?.cancel_contract_close_button,
              t('texts:cancel_contract:close_button')
            )}
          </Button>
        </div>
      ) : (
        <form
          className={clsx('cancel-contract-modal__content', classes.content)}
          onSubmit={handleSubmit}
          noValidate
        >
          <Typography component="p" className={classes.intro}>
            {prismicText(
              data?.cancel_contract_intro,
              t('texts:cancel_contract:intro')
            )}
          </Typography>

          <FormControl component="fieldset" className={classes.typeGroup}>
            <Typography
              component="legend"
              variant="subtitle2"
              className={classes.typeLabel}
            >
              {prismicText(
                data?.cancel_contract_type_label,
                t('texts:cancel_contract:type_label')
              )}
            </Typography>
            <RadioGroup
              aria-label="contact-request-type"
              name="contact-request-type"
              value={type}
              onChange={(e) => setType(e.target.value as ContactRequestType)}
            >
              <FormControlLabel
                value="CANCELLATION"
                control={<Radio color="primary" />}
                label={prismicText(
                  data?.cancel_contract_type_cancellation,
                  t('texts:cancel_contract:type_cancellation')
                )}
              />
              <FormControlLabel
                value="RETURN"
                control={<Radio color="primary" />}
                label={prismicText(
                  data?.cancel_contract_type_return,
                  t('texts:cancel_contract:type_return')
                )}
              />
            </RadioGroup>
          </FormControl>

          <TextField
            name="cancel-contract-order-reference"
            type="text"
            label={prismicText(
              data?.cancel_contract_order_reference_label,
              t('texts:cancel_contract:order_reference_label')
            )}
            value={orderReference}
            onChange={(value) => setOrderReference(value)}
            required
            maxLength={255}
          />
          {errors.orderReference && (
            <Typography component="span" className={classes.error}>
              {errors.orderReference}
            </Typography>
          )}

          <TextField
            name="cancel-contract-email"
            type="email"
            label={prismicText(
              data?.cancel_contract_email_label,
              t('texts:cancel_contract:email_label')
            )}
            value={email}
            onChange={(value) => setEmail(value)}
            required
            maxLength={255}
          />
          {errors.email && (
            <Typography component="span" className={classes.error}>
              {errors.email}
            </Typography>
          )}

          <TextField
            className={classes.messageField}
            name="cancel-contract-message"
            label={prismicText(
              data?.cancel_contract_message_label,
              t('texts:cancel_contract:message_label')
            )}
            value={message}
            onChange={(value) => setMessage(value)}
            textArea
            multiline
            rows={3}
            maxLength={5000}
          />

          {errors.global && (
            <Typography className={classes.globalError}>
              {errors.global}
            </Typography>
          )}

          <Typography component="span" className={classes.required}>
            {t('texts:cancel_contract:required_fields')}
          </Typography>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            className={clsx('cancel-contract-modal__submit', classes.submit)}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              prismicText(
                data?.cancel_contract_submit_button,
                t('texts:cancel_contract:submit_button')
              )
            )}
          </Button>
        </form>
      )}
    </Dialog>
  )
}

export default CancelContractModal
